import Button from '@risksmart-app/components/src/button';
import type Highcharts from 'highcharts';
import type { HighchartsReactRefObject } from 'highcharts-react-official';
import type { Ref } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useDashboardWidgetSettings } from '../../../../context/useDashboardWidgetSettings';
import { HighchartsWidget } from '../../HighchartsWidget';
import type { WidgetRef } from '../../types';
import { useWidgetSettingsHandler } from '../../widget-context/useWidgetSettingsHandler';
import { EditorModal } from './EditorModal';
import {
  buildRichTextWidgetCss,
  buildRichTextWidgetHtml,
  sanitiseRichTextContent,
} from './richTextContent';
import type { WidgetSettings } from './widgetSettingsSchema';

export const RichTextWidget = (_props: unknown, ref: Ref<WidgetRef>) => {
  const [settings, setSettings] = useDashboardWidgetSettings<WidgetSettings>();
  const [showModal, setShowModal] = useState(false);
  const textEl = useRef<Highcharts.SVGElement | null>(null);

  const { t } = useTranslation('common', {
    keyPrefix: 'dashboard.widgets.richText',
  });

  useWidgetSettingsHandler(ref, () => setShowModal(true));

  const handleSave = async (data: WidgetSettings) => {
    setSettings({ ...data, content: sanitiseRichTextContent(data.content) });
  };

  // `useStableChartOptions` collapses value-equal options to a stable reference and
  // deliberately treats all functions as equal, so a change visible only inside
  // `events.render` is neither redrawn nor re-applied — its docstring requires callers
  // to "stabilise upstream". Two consequences to handle, or editing a saved widget
  // leaves the old content on screen until remount (the "exit and re-enter the
  // platform" workaround noted on CUS-398):
  //
  //  1. The chart keeps the render callback it was built with, so that closure must not
  //     capture the content — it reads this ref, which is always current.
  //  2. Nothing triggers a redraw, so the effect below asks for one when content
  //     changes, which re-runs the callback.
  const content = settings?.content;
  const contentRef = useRef(content);
  contentRef.current = content;

  const chartRef = useRef<HighchartsReactRefObject | null>(null);

  useEffect(() => {
    chartRef.current?.chart?.redraw(false);
  }, [content]);

  const options: Highcharts.Options = {
    chart: {
      type: 'richText',
      animation: false,
      events: {
        render: function () {
          if (textEl.current) {
            textEl.current.destroy();
          }
          const renderer = this.renderer;
          textEl.current = renderer
            .text(
              buildRichTextWidgetHtml(contentRef.current, this.plotWidth),
              0,
              0,
              true
            )
            .css(buildRichTextWidgetCss(this.plotHeight))
            .add();
        },
      },
    },
  };

  return (
    <>
      {showModal && (
        <EditorModal
          onDismiss={() => setShowModal(false)}
          onSave={handleSave}
        />
      )}
      <div className={'rich-text-widget'} style={{ height: '100%' }}>
        {!settings ||
        (settings.allowOwnershipFiltering && !settings.content) ? (
          <div className={'flex flex-col justify-center items-center'}>
            <h3 className={'m-0'}>{t('not_configured_title')}</h3>
            <p>{t('not_configured')}</p>
            <Button onClick={() => setShowModal(true)}>
              {t('configure_button')}
            </Button>
          </div>
        ) : (
          <HighchartsWidget options={options} ref={chartRef} />
        )}
      </div>
    </>
  );
};
